import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import screens from '@loggi/ui/components/utils/screens';
import Autosuggest from '@loggi/react-autosuggest';
import Icon from '@loggi/ui/components/icon';
import Display from '@loggi/ui/components//display';
import View from '@loggi/ui/components/view';
import icons from '@loggi/ui/components/utils/icons';
import InputWrapper from '../components/input-wrapper';
import validateSource from '../utils/validate-source';
import {
  defaultGetSuggestionValue,
  defaultRenderSuggestion
} from '../utils/suggestions';
import {
  AUXILIAR_COMPONENT_POSITIONS,
  AUXILIAR_COMPONENT_POSITION_BOTTOM
} from '../utils/constants';
import { SIZES } from '../types';
import styles from './styles.scss';

const noop = () => {};
const INPUT_TYPE = 'search';
const ENTER_KEY = 'Enter';
const CLASS_CONTAINER_WITH_LABEL = 'typeaheadSuggestionContainerWithLabel';
const CLASS_CONTAINER = 'typeaheadSuggestionContainer';
const LARGE_SIZE = 'large';

class Typeahead extends React.Component {
  static displayName = 'Typeahead';

  static propTypes = {
    onSuggestionsOpen: PropTypes.func,
    onSuggestionsClose: PropTypes.func,
    size: PropTypes.oneOf(SIZES),
    label: PropTypes.string,
    dataSources: PropTypes.array,
    getSuggestionValue: PropTypes.func,
    renderSuggestion: PropTypes.func,
    parseQuery: PropTypes.func,
    renderValue: PropTypes.func,
    onSuggestionSelected: PropTypes.func,
    onChange: PropTypes.func,
    selectFirst: PropTypes.bool,
    placeholder: PropTypes.string,
    auxiliarComponent: PropTypes.element,
    inputProps: PropTypes.object,
    initialValue: PropTypes.string,
    auxiliarComponentPosition: PropTypes.oneOf(AUXILIAR_COMPONENT_POSITIONS),
    icon: PropTypes.oneOf(icons.names),
    hasFocus: PropTypes.bool,
    preventKeyEvent: PropTypes.bool,
    customRenderInput: PropTypes.func,
    customRenderList: PropTypes.func,
    theme: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    validateInputToAutocomplete: PropTypes.bool
  };

  static defaultProps = {
    label: null,
    getSuggestionValue: defaultGetSuggestionValue,
    renderSuggestion: defaultRenderSuggestion,
    onSuggestionSelected: noop,
    onChange: noop,
    selectFirst: false,
    placeholder: '',
    inputProps: {},
    initialValue: '',
    theme: {},
    auxiliarComponentPosition: AUXILIAR_COMPONENT_POSITION_BOTTOM,
    icon: null,
    preventKeyEvent: false,
    validateInputToAutocomplete: false
  };

  constructor(props) {
    super(props);

    this._onChange = this._onChange.bind(this);
    this._onSuggestionSelected = this._onSuggestionSelected.bind(this);
    this._handleOnEnter = this._handleOnEnter.bind(this);
    this._shouldAutocomplete = this._shouldAutocomplete.bind(this);

    this.sourcesData = [];

    this.state = {
      value: props.initialValue,
      suggestions: [],
      typedLetters: 0
    };
  }

  _parseSourcesData(value) {
    const { dataSources } = this.props;
    const sources = dataSources.map((source, index) => {
      const bindedSuggestions = this._updateSuggestions.bind(this, index);
      const sourceSuggestions = source(value, bindedSuggestions);

      validateSource(sourceSuggestions);
      return sourceSuggestions;
    });

    this.sourcesData = sources;
    this._updateSuggestions();
  }

  _updateSuggestions(index = null, newSuggestions) {
    if (typeof index === 'number') {
      this.sourcesData[index] = newSuggestions;
    }

    const suggestions = this.sourcesData.reduce(
      (arr, sourceData) => [...arr, ...sourceData],
      []
    );

    this.setState({
      suggestions
    });
  }

  _onChange(e, preventOnChange) {
    const { onChange, preventKeyEvent, parseQuery } = this.props;

    if (e.key === ENTER_KEY && preventKeyEvent) {
      e.preventDefault();
      preventOnChange = true;
    }

    const { value: stateValue } = this.state;
    const { value } = e.target;

    if (value !== stateValue && typeof value === 'string') {
      if (this._shouldAutocomplete(value)) {
        this._parseSourcesData(parseQuery ? parseQuery(value) : value);
      }
      this.setState({ value });
    }

    if (preventOnChange !== true && onChange) {
      onChange(value);
    }
  }

  _shouldAutocomplete(value) {
    let { typedLetters } = this.state;
    const lastCharacter = value.slice(-1);
    const NUM_TYPED_LETTERS = 4;
    const MAX_CHARACTERS = 100;

    if (!this.props.validateInputToAutocomplete) {
      return true;
    }

    const isSpecialCharacterTyped = !/[a-zA-Z0-9]/.test(lastCharacter);
    const isMaxCharacterReached = value.length >= MAX_CHARACTERS;
    const isNumberTyped = /[0-9]/.test(lastCharacter);

    if (isMaxCharacterReached || isSpecialCharacterTyped) {
      return false;
    }

    if (isNumberTyped) {
      return true;
    }

    typedLetters += 1;

    const shouldAutocomplete = typedLetters === NUM_TYPED_LETTERS;
    typedLetters = shouldAutocomplete ? 0 : typedLetters;
    this.setState({ typedLetters });

    return shouldAutocomplete;
  }

  _onSuggestionSelected(e, { suggestion: s, suggestionIndex }) {
    const { getSuggestionValue, onSuggestionSelected } = this.props;

    this.setState({ value: getSuggestionValue(s) });
    onSuggestionSelected(s, { suggestionIndex });
  }

  _getTheme() {
    const theme = this.props.theme;
    const label = this.props.label;
    const hasLabel = label !== null;
    const suggestionsContainerTheme = hasLabel
      ? CLASS_CONTAINER_WITH_LABEL
      : CLASS_CONTAINER;

    const typeaheadTheme = {
      suggestionsContainer: suggestionsContainerTheme,
      suggestion: 'typeaheadSuggestionItem',
      suggestionFocused: 'suggestionFocused',
      itemsListContainer: 'typeaheadSuggestionItemList'
    };

    let finalTheme = {};
    if (typeof theme === 'function') {
      finalTheme = theme(typeaheadTheme);
    } else if (typeof theme === 'object') {
      finalTheme = {
        ...typeaheadTheme,
        ...theme
      };
    }

    return finalTheme;
  }

  _handleOnEnter(e) {
    if (screens.isMobile() && e.key === ENTER_KEY) {
      e.preventDefault();
      screens.hideMobileKeyboard();
    }
  }

  UNSAFE_componentWillUpdate(nextProps) {
    if (this.props.initialValue !== nextProps.initialValue) {
      this._onChange(
        {
          target: { value: nextProps.initialValue }
        },
        true
      );
    }
  }

  render() {
    const { value, suggestions } = this.state;

    const {
      label,
      getSuggestionValue,
      renderSuggestion,
      selectFirst,
      placeholder,
      auxiliarComponent,
      auxiliarComponentPosition,
      inputProps,
      icon,
      size,
      hasFocus,
      customRenderInput,
      customRenderList
    } = this.props;

    const hasIcon = icon !== null && icon.trim() !== '';
    const theme = this._getTheme();

    const classesContainer = classNames(styles.container, {
      [styles.withIcon]: hasIcon,
      [styles.large]: size === LARGE_SIZE
    });

    return (
      <View className={classesContainer}>
        <Display when={hasIcon}>
          {() => <Icon type={icon} className={styles.typeaheadIcon} />}
        </Display>
        <Autosuggest
          customInput={InputWrapper}
          auxiliarComponent={auxiliarComponent}
          auxiliarComponentPosition={auxiliarComponentPosition}
          selectFirst={screens.isMobile() ? false : selectFirst}
          getSuggestionValue={getSuggestionValue}
          renderSuggestion={renderSuggestion}
          suggestions={suggestions}
          onSuggestionSelected={this._onSuggestionSelected}
          customRenderInput={customRenderInput}
          customRenderList={customRenderList}
          theme={theme}
          inputProps={{
            label,
            value: this.props.renderValue
              ? this.props.renderValue(value)
              : value,
            onChange: this._onChange,
            onKeyUp: this._handleOnEnter,
            placeholder,
            size,
            hasFocus,
            onSuggestionsOpen: this.props.onSuggestionsOpen,
            onSuggestionsClose: this.props.onSuggestionsClose,
            type: INPUT_TYPE,
            ...inputProps
          }}
        />
      </View>
    );
  }
}

export default Typeahead;
